import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import isNil from 'lodash-es/isNil';
import omitBy from 'lodash-es/omitBy';
import { AppConfig } from 'src/app/app.config';
import { Profile } from '../../models/profile.model';
import { ConnectService } from '../connect/connect.service';

export interface Communication {
  name: string;
  id: string;
}
export interface Option {
  name: string;
  id: boolean;
}

export enum CommunicationPreference {
  SMS = 'sms',
  EMAIL = 'email',
  NONE = 'none',
}

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  public profile: Profile = null;
  public preference = '';

  public showSelects: boolean;

  public communicationType: Communication[] = [
    { name: 'ProfileService.sms', id: CommunicationPreference.SMS },
    { name: 'ProfileService.email', id: CommunicationPreference.EMAIL },
  ];
  public optoutOptions: Option[] = [
    { name: 'ProfileService.yes', id: false },
    { name: 'ProfileService.no', id: true },
  ];

  get currentLocale() {
    return this.profile?.preferred_language?.locale || this.profile?.organisation?.default_language?.locale || 'en';
  }

  constructor(public connectSvc: ConnectService, public translate: TranslateService, @Inject(AppConfig) public config: AppConfig) {
    this.setTranslation();

    if (this.config.SHOW_NONE_COMM_PREFERENCE) {
      this.communicationType.push({ name: 'ProfileService.none', id: CommunicationPreference.NONE });
    }
  }

  getNonSensitiveProfile(profile: Partial<Profile> = this.profile): Partial<Profile> {
    const { jwt, organisation, weguide_id, ...rest } = profile;
    let payload = { ...omitBy(rest, isNil) };
    if (organisation) {
      delete organisation.logo_url;
      delete organisation.letter_body;
      payload = { ...payload, organisation: omitBy(organisation, isNil) };
    }
    return payload;
  }

  public getProfile() {
    return new Promise(resolve => {
      this.connectSvc.getCustom(`/accounts/profile`).subscribe(
        (profile: Profile) => {
          this.profile = profile;
          resolve(profile);
        },
        error => {
          this.connectSvc.handleError(error);
          resolve(null);
        }
      );
    });
  }

  setProfileField(field: string, value: string | number) {
    return new Promise(resolve => {
      this.connectSvc
        .update('/accounts/profile', null, {
          account: { [field]: value },
        })
        .subscribe({
          next: async () => {
            await this.getProfile();
            resolve(null);
          },
          error: error => {
            this.connectSvc.handleError(error);
            resolve(null);
          },
        });
    });
  }

  public setPreference(preference: string, optout: boolean) {
    const communication_preferences = preference === CommunicationPreference.NONE ? [] : [preference];

    return new Promise(resolve => {
      this.connectSvc
        .update('/accounts/profile', null, {
          account: { communication_preferences, opt_out: optout },
        })
        .subscribe(
          () => {
            resolve(null);
          },
          error => {
            this.connectSvc.handleError(error);
            resolve(null);
          }
        );
    });
  }

  public setOptOutOption(optout: boolean) {
    return new Promise(resolve => {
      this.connectSvc
        .update(`/accounts/profile`, null, {
          account: { opt_out: optout },
        })
        .subscribe(
          () => {
            resolve(null);
          },
          error => {
            this.connectSvc.handleError(error);
            resolve(null);
          }
        );
    });
  }

  public async getProfileRecordId(): Promise<number> {
    let recordid;
    await this.profile.record.reduce(async (previousRecord, record) => {
      await previousRecord;
      recordid = record.id;
    }, Promise.resolve());
    return recordid;
  }

  public setTranslation() {
    this.showSelects = false;
    window.setTimeout(() => {
      this.showSelects = true;
    }, 1);
  }
}
